<template>
  <div class="notification-content" :class="contentClass">
    <!-- Main Row: Icon, Message, Controls -->
    <div class="notification-content__main-row">
      <NotificationIcon 
        :severity="notification.severity" 
        :size="16"
        class="notification-content__icon"
      />
      
      <div class="notification-content__message-area">
        <NotificationMessage 
          :message="notification.message"
          :expandable="notification.expandable"
          :expanded="notification.expanded"
          :max-lines="1"
          @expand="handleExpand"
          @collapse="handleCollapse"
          class="notification-content__message"
        />
      </div>
      
      <!-- Single Expand/Collapse button and Close button -->
      <div class="notification-content__controls">
        <button
          v-if="notification.expandable"
          class="notification-content__expand-button"
          @click="notification.expanded ? handleCollapse() : handleExpand()"
          :aria-label="notification.expanded ? 'Collapse notification' : 'Expand notification'"
          type="button"
        >
          <Icon :name="notification.expanded ? 'caret-down' : 'caret-up'" :size="12" />
        </button>
        
        <button
          v-if="!notification.progress"
          class="notification-content__close-button"
          @click="handleClose"
          :aria-label="'Close notification'"
          type="button"
        >
          <Icon name="x" :size="12" />
        </button>
      </div>
    </div>
    
    <!-- Source + Actions Row (always shown if either exists) -->
    <div 
      v-if="sourceLabel || hasAnyActions"
      class="notification-content__details-row"
    >
      <!-- Source (left-aligned) -->
      <div 
        v-if="sourceLabel"
        class="notification-content__source"
        :title="sourceTooltip"
      >
        {{ sourceLabel }}
      </div>
      
      <!-- Actions (right-aligned) -->
      <div 
        v-if="hasAnyActions"
        class="notification-content__actions-container"
      >
        <NotificationActionButton
          v-for="action in (notification.actions?.primary || [])"
          :key="action.id"
          :action="action"
          variant="secondary"
          size="small"
          @action="handleAction"
          class="notification-content__action-button"
        />
        <NotificationActionButton
          v-for="action in (notification.actions?.secondary || [])"
          :key="action.id"
          :action="action"
          variant="secondary"
          size="small"
          @action="handleAction"
          class="notification-content__action-button"
        />
      </div>
    </div>
    
    <!-- Progress Bar (if present) -->
    <ProgressBar
      v-if="notification.progress"
      :value="progressValue"
      :infinite="isInfiniteProgress"
      :size="'small'"
      class="notification-content__progress"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Icon from '../atoms/Icon.vue';
import NotificationIcon from '../atoms/NotificationIcon.vue';
import NotificationMessage from '../atoms/NotificationMessage.vue';
import NotificationActionButton from '../atoms/NotificationActionButton.vue';
import ProgressBar from '../atoms/ProgressBar.vue';
import type { ToastNotification } from '@/types/notifications';

export interface NotificationContentProps {
  notification: ToastNotification;
}

const props = defineProps<NotificationContentProps>();

const emit = defineEmits<{
  expand: [];
  collapse: [];
  close: [];
  action: [actionId: string];
}>();

const contentClass = computed(() => ({
  'notification-content--expanded': props.notification.expanded,
  'notification-content--has-progress': !!props.notification.progress,
  'notification-content--has-actions': hasAnyActions.value,
  [`notification-content--${props.notification.severity}`]: true,
}));

const sourceLabel = computed(() => {
  if (!props.notification.source) return '';
  
  return typeof props.notification.source === 'string' 
    ? props.notification.source 
    : props.notification.source.label;
});

const sourceTooltip = computed(() => {
  if (!props.notification.source || typeof props.notification.source === 'string') {
    return sourceLabel.value;
  }
  
  return `${props.notification.source.label} (${props.notification.source.id})`;
});

const hasPrimaryActions = computed(() => {
  return props.notification.actions?.primary && props.notification.actions.primary.length > 0;
});

const hasSecondaryActions = computed(() => {
  return props.notification.actions?.secondary && props.notification.actions.secondary.length > 0;
});

const hasAnyActions = computed(() => {
  return hasPrimaryActions.value || hasSecondaryActions.value;
});

const progressValue = computed(() => {
  if (!props.notification.progress) return 0;
  
  const { total = 100, worked = 0 } = props.notification.progress;
  return total > 0 ? (worked / total) * 100 : 0;
});

const isInfiniteProgress = computed(() => {
  return props.notification.progress?.infinite ?? false;
});

const handleExpand = () => {
  emit('expand');
};

const handleCollapse = () => {
  emit('collapse');
};

const handleClose = () => {
  emit('close');
};

const handleAction = (actionId: string) => {
  emit('action', actionId);
};
</script>

<style scoped lang="scss">
.notification-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0; // Allow text truncation
  
  &__main-row {
    display: flex;
    align-items: flex-start; // Changed from center to anchor to top
    gap: var(--spacing-sm);
    min-height: 22px;
    padding: var(--spacing-sm);
  }
  
  &__icon {
    flex-shrink: 0;
    margin-top: 1px; // Slight alignment adjustment
  }
  
  &__message-area {
    flex: 1;
    min-width: 0;
    padding-top: 1px; // Align with icon
  }
  
  &__message {
    flex: 1;
    min-width: 0;
  }
  
  &__controls {
    display: flex;
    align-items: flex-start; // Anchor to top
    gap: 4px;
    flex-shrink: 0;
    margin-left: var(--spacing-xs);
    padding-top: 1px; // Align with icon/message baseline
  }
  
  &__expand-button,
  &__close-button {
    flex-shrink: 0;
    background: none;
    border: none;
    color: var(--vscode-textLink-foreground);
    cursor: pointer;
    padding: 1px 2px;
    margin: 0;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    
    &:hover {
      background-color: var(--vscode-toolbar-hoverBackground);
    }
    
    &:focus {
      outline: 1px solid var(--vscode-focusBorder);
      outline-offset: 1px;
    }
  }
  
  &__details-row {
    display: flex;
    align-items: center;
    padding: var(--spacing-xs) 0 0 0; // Padding-left aligns with message content
    gap: var(--spacing-sm);
  }
  
  &__source {
    font-size: 11px;
    color: var(--vscode-descriptionForeground);
    opacity: 0.8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 1;
    min-width: 0;
  }
  
  &__actions-container {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    flex-wrap: wrap;
    flex-shrink: 0;
    margin-left: auto; // Always push actions to the right
  }
  
  &__progress {
    margin-top: var(--spacing-xs);
  }
  
  // Severity-based styling - more subtle left border
  &--success {
    border-left: 2px solid var(--vscode-charts-green);
    padding-left: var(--spacing-xs);
  }
  
  &--warning {
    border-left: 2px solid var(--vscode-charts-orange);
    padding-left: var(--spacing-xs);
  }
  
  &--error {
    border-left: 2px solid var(--vscode-charts-red);
    padding-left: var(--spacing-xs);
  }
  
  &--info {
    border-left: 2px solid var(--vscode-charts-blue);
    padding-left: var(--spacing-xs);
  }
}
</style>
